// Fast fri: en steamer har fri på bestemte ugedage eller tidsrum, fx "fri hver mandag" eller "fri tirsdage efter kl. 14".
//
// Regler:
//  - Kundens booking og Smartplans forslag tager aldrig en tid, der rører steamerens faste fri (ledige.ts).
//  - Kontoret kan stadig lægge en booking i den faste fri manuelt – appen spørger først, og det logges.
//  - En særlig dag (Team › Steamere) går forud for den faste fri den dag, ligesom den går forud for ugeplanen.
//  - Tider i minutter efter midnat. Hele dagen = 0–1440. "Efter kl. 14" = 840–1440. "Før kl. 10" = 0–600.
import { fejl } from './fejl.ts';
import { klokken as kl, ugedagMandag1 } from './dato.ts';
import type { SaerligDag, Steamer } from './typer.ts';

export type FastFri = { ugedag: number; fra: number; til: number; note?: string };
/** Steameren beder selv om fast fri i appen. Kontoret godkender (så lægges reglen i steamerens faste fri) eller afviser. */
export type FastFriAnmodning = FastFri & {
  id: string; steamer_id: string; status: 'afventer' | 'godkendt' | 'afvist'; oprettet: string;
  behandlet?: string | null; behandlet_af?: string | null; grund?: string | null;
};
/** Formularens valg: hele dagen, efter kl., før kl. eller et tidsrum. */
export type FriValg = 'dag' | 'efter' | 'foer' | 'tidsrum';

const UGEDAGE = ['mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag', 'søndag'];
const UGEDAGE_FLERTAL = ['mandage', 'tirsdage', 'onsdage', 'torsdage', 'fredage', 'lørdage', 'søndage'];
export const ugedagNavn = (ugedag: number) => UGEDAGE[ugedag - 1] ?? '';

export const heleDagen = (f: FastFri) => f.fra <= 0 && f.til >= 1440;

/** "hver mandag", "tirsdage efter kl. 14:00", "onsdage før kl. 10:00", "torsdage kl. 12:00–14:00". */
export function friTekst(f: FastFri): string {
  const d = UGEDAGE_FLERTAL[f.ugedag - 1] ?? '';
  if (heleDagen(f)) return `hver ${ugedagNavn(f.ugedag)}`;
  if (f.til >= 1440) return `${d} efter kl. ${kl(f.fra)}`;
  if (f.fra <= 0) return `${d} før kl. ${kl(f.til)}`;
  return `${d} kl. ${kl(f.fra)}–${kl(f.til)}`;
}

/** Som friTekst, men med stort begyndelsesbogstav: "Hver mandag", "Tirsdage efter kl. 14:00". */
export const friOverskrift = (f: FastFri) => friTekst(f).replace(/^./, (c) => c.toUpperCase());

/** Formularens valg → minutter. `kl` er "efter"/"før"/"fra"-tiden, `tilKl` slutningen af et tidsrum. Null, hvis en tid mangler. */
export function friFraValg(valg: FriValg, kl: number | null, tilKl: number | null): { fra: number; til: number } | null {
  if (valg === 'dag') return { fra: 0, til: 1440 };
  if (kl == null) return null;
  if (valg === 'efter') return { fra: kl, til: 1440 };
  if (valg === 'foer') return { fra: 0, til: kl };
  return tilKl == null ? null : { fra: kl, til: tilKl };
}

/** Steamerens faste fri på datoen (tom, hvis han har en særlig dag den dag). */
export function friPaa(s: Pick<Steamer, 'id' | 'fastFri'>, dato: string, saerligeDage: SaerligDag[] = []): FastFri[] {
  if (!s.fastFri?.length) return [];
  if (saerligeDage.some((x) => x.steamer_id === s.id && x.dato === dato)) return [];
  const w = ugedagMandag1(dato);
  return s.fastFri.filter((f) => f.ugedag === w);
}

/** Den faste fri, et tidsrum rører – eller null. */
export function iFastFri(s: Pick<Steamer, 'id' | 'fastFri'>, dato: string, start: number, slut: number, saerligeDage: SaerligDag[] = []): FastFri | null {
  return friPaa(s, dato, saerligeDage).find((f) => f.fra < slut && f.til > start) ?? null;
}

/** Tjek og ryd op i listen (højst 14, sorteret, ingen dubletter). */
export function normFastFri(l: FastFri[]): FastFri[] {
  if (!Array.isArray(l) || l.length > 14) throw fejl('Højst 14 faste fridage/tidsrum pr. steamer.');
  const ud: FastFri[] = [];
  for (const x of l) {
    const ugedag = Math.round(Number(x.ugedag)), fra = Math.round(Number(x.fra)), til = Math.round(Number(x.til));
    if (!(ugedag >= 1 && ugedag <= 7)) throw fejl('Vælg en ugedag.');
    if (!(fra >= 0 && til <= 1440 && til > fra)) throw fejl('Fri til skal være efter fri fra.');
    const note = String(x.note ?? '').trim().slice(0, 100);
    if (!ud.some((y) => y.ugedag === ugedag && y.fra === fra && y.til === til)) ud.push({ ugedag, fra, til, ...(note ? { note } : {}) });
  }
  return ud.sort((a, b) => a.ugedag - b.ugedag || a.fra - b.fra);
}
